import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { LifeBuoy, Plus, Send, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import AppShell from "../components/AppShell";
import { LoadingState } from "../components/LoadingState";
import api, { formatApiError } from "../lib/api";

const CATEGORIES = [
  ["account", "Account access"],
  ["billing", "Billing"],
  ["crm_connection", "CRM connection"],
  ["scan", "Scan or report"],
  ["writeback", "Approved correction"],
  ["security", "Security concern"],
  ["other", "Other"],
];

const STATUS_STYLE = {
  open: "bg-blue-50 text-blue-700",
  in_progress: "bg-violet-50 text-violet-700",
  waiting_customer: "bg-amber-50 text-amber-700",
  resolved: "bg-emerald-50 text-emerald-700",
  closed: "bg-slate-100 text-slate-600",
};

function formatDate(value) {
  return value ? new Date(value).toLocaleString() : "—";
}

export default function Support() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTicket = searchParams.get("ticket");
  const [tickets, setTickets] = useState([]);
  const [connections, setConnections] = useState([]);
  const [scans, setScans] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [reply, setReply] = useState("");
  const [form, setForm] = useState({
    subject: "", description: "", category: "other", connection_id: "", scan_id: "",
  });

  const loadTicket = useCallback(async (ticketId) => {
    if (!ticketId) { setSelected(null); return; }
    try {
      const { data } = await api.get(`/support/tickets/${ticketId}`);
      setSelected(data);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
      setSearchParams({}, { replace: true });
    }
  }, [setSearchParams]);

  const load = useCallback(async () => {
    try {
      const [{ data: ticketData }, { data: connectionData }, { data: scanData }] =
        await Promise.all([
          api.get("/support/tickets"),
          api.get("/connections"),
          api.get("/scans"),
        ]);
      setTickets(ticketData.items || []);
      setConnections(connectionData || []);
      setScans(scanData || []);
      if (requestedTicket) await loadTicket(requestedTicket);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally { setLoading(false); }
  }, [loadTicket, requestedTicket]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const draft = location.state?.supportDraft;
    if (!draft) return;
    setForm((current) => ({
      ...current,
      subject: draft.subject || "",
      description: draft.description || "",
      category: draft.category || "other",
    }));
    setSelected(null);
    setShowNew(true);
    setSearchParams({});
  }, [location.state, setSearchParams]);

  const filteredScans = useMemo(
    () => scans.filter((scan) => !form.connection_id || scan.connection_id === form.connection_id),
    [scans, form.connection_id],
  );

  const chooseTicket = async (ticketId) => {
    setShowNew(false);
    setSearchParams({ ticket: ticketId });
    await loadTicket(ticketId);
  };

  const createTicket = async (event) => {
    event.preventDefault();
    setBusy("create");
    try {
      const payload = {
        ...form,
        connection_id: form.connection_id || null,
        scan_id: form.scan_id || null,
      };
      const { data } = await api.post("/support/tickets", payload);
      setTickets((items) => [data, ...items]);
      setSelected(data);
      setShowNew(false);
      setForm({ subject: "", description: "", category: "other", connection_id: "", scan_id: "" });
      setSearchParams({ ticket: data.id });
      toast.success(`Support ticket ${data.reference} created.`);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const sendReply = async (event) => {
    event.preventDefault();
    if (!reply.trim() || !selected) return;
    setBusy("reply");
    try {
      await api.post(`/support/tickets/${selected.id}/messages`, { body: reply });
      setReply("");
      await Promise.all([loadTicket(selected.id), load()]);
      toast.success("Reply added.");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally { setBusy(""); }
  };

  if (loading) return <AppShell><LoadingState label="Loading support" /></AppShell>;

  return <AppShell>
    <div className="space-y-6" data-testid="support-page">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-blue-600">Account-linked assistance</div>
          <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight">Support Center</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">Every request is attached to your CleanIQ organization and can include the exact CRM and scan involved.</p>
        </div>
        <button data-testid="support-new-ticket" onClick={() => { setShowNew(true); setSelected(null); setSearchParams({}); }}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-700">
          <Plus className="h-4 w-4" /> New support ticket
        </button>
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        Never submit a CRM API key, password, authentication code, or payment-card number.
      </div>

      <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
        <aside className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-5 py-4">
            <h2 className="font-heading text-lg font-semibold">Your requests</h2>
            <p className="text-xs text-slate-500">{tickets.length} visible ticket{tickets.length === 1 ? "" : "s"}</p>
          </div>
          <div className="max-h-[650px] divide-y divide-slate-100 overflow-auto">
            {tickets.length === 0 && <div className="p-8 text-center text-sm text-slate-500">
              <LifeBuoy className="mx-auto mb-3 h-7 w-7 text-slate-300" />No support tickets yet.
            </div>}
            {tickets.map((ticket) => <button key={ticket.id} data-testid={`support-ticket-${ticket.id}`}
              onClick={() => chooseTicket(ticket.id)}
              className={"w-full p-4 text-left transition hover:bg-slate-50 " + (selected?.id === ticket.id ? "bg-blue-50" : "")}>
              <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-[10px] font-semibold text-slate-500">{ticket.reference}</span>
                <span className={`rounded-full px-2 py-1 text-[10px] font-semibold capitalize ${STATUS_STYLE[ticket.status] || STATUS_STYLE.open}`}>
                  {ticket.status.replaceAll("_", " ")}
                </span>
              </div>
              <div className="mt-2 truncate text-sm font-semibold text-slate-900">{ticket.subject}</div>
              <div className="mt-1 line-clamp-2 text-xs leading-5 text-slate-500">{ticket.last_message_preview}</div>
              <div className="mt-2 text-[10px] text-slate-400">{formatDate(ticket.updated_at)}</div>
            </button>)}
          </div>
        </aside>

        <section className="min-h-[520px] rounded-2xl border border-slate-200 bg-white shadow-sm">
          {showNew ? <form onSubmit={createTicket} className="p-6" data-testid="support-create-form">
            <h2 className="font-heading text-2xl font-semibold">Tell us what happened</h2>
            <p className="mt-1 text-sm text-slate-500">Choose the affected CRM or scan when possible so support can investigate the correct account immediately.</p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <label className="text-sm font-medium">Category
                <select value={form.category} onChange={(e) => setForm({...form, category:e.target.value})}
                  className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5">
                  {CATEGORIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
              <label className="text-sm font-medium">CRM connection
                <select value={form.connection_id}
                  onChange={(e) => setForm({...form, connection_id:e.target.value, scan_id:""})}
                  className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5">
                  <option value="">Not specific to one CRM</option>
                  {connections.map((connection) => <option key={connection.id} value={connection.id}>{connection.label} · {connection.connector_name}</option>)}
                </select>
              </label>
              <label className="text-sm font-medium sm:col-span-2">Related scan
                <select value={form.scan_id} onChange={(e) => setForm({...form, scan_id:e.target.value})}
                  className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5">
                  <option value="">No related scan</option>
                  {filteredScans.map((scan) => <option key={scan.id} value={scan.id}>{scan.id} · {scan.status} · {formatDate(scan.started_at)}</option>)}
                </select>
              </label>
              <label className="text-sm font-medium sm:col-span-2">Subject
                <input data-testid="support-subject" required minLength={5} maxLength={160}
                  value={form.subject} onChange={(e) => setForm({...form, subject:e.target.value})}
                  className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2.5" />
              </label>
              <label className="text-sm font-medium sm:col-span-2">Description
                <textarea data-testid="support-description" required minLength={20} maxLength={5000} rows={8}
                  value={form.description} onChange={(e) => setForm({...form, description:e.target.value})}
                  placeholder="What were you trying to do, what happened, and what did you expect?"
                  className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2.5" />
              </label>
            </div>
            <button data-testid="support-submit" disabled={busy === "create"}
              className="mt-5 rounded-lg bg-blue-600 px-5 py-3 text-sm font-semibold text-white disabled:opacity-50">
              {busy === "create" ? "Creating…" : "Create support ticket"}
            </button>
          </form> : selected ? <div className="flex min-h-[620px] flex-col" data-testid="support-ticket-detail">
            <div className="border-b border-slate-200 p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div><div className="font-mono text-xs text-blue-600">{selected.reference}</div><h2 className="mt-1 font-heading text-2xl font-semibold">{selected.subject}</h2></div>
                <span className={`rounded-full px-3 py-1.5 text-xs font-semibold capitalize ${STATUS_STYLE[selected.status] || STATUS_STYLE.open}`}>
                  {selected.status.replaceAll("_", " ")}
                </span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-500">
                <span className="rounded-full bg-slate-100 px-3 py-1 capitalize">{selected.category.replaceAll("_", " ")}</span>
                {selected.connection_label && <span className="rounded-full bg-slate-100 px-3 py-1">{selected.connection_label} · {selected.connector_name}</span>}
                {selected.scan_id && <span className="rounded-full bg-slate-100 px-3 py-1 font-mono">{selected.scan_id}</span>}
              </div>
            </div>
            <div className="flex-1 space-y-4 overflow-auto bg-slate-50 p-5">
              {(selected.messages || []).map((message) => <article key={message.id}
                className={"max-w-3xl rounded-2xl border p-4 " + (message.author_role === "support" ? "border-blue-200 bg-blue-50" : "ml-auto border-slate-200 bg-white")}>
                <div className="flex items-center justify-between gap-3 text-xs">
                  <span className="font-semibold text-slate-800">{message.author_role === "support" ? "CleanIQ Support" : message.author_name}</span>
                  <span className="text-slate-400">{formatDate(message.created_at)}</span>
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700">{message.body}</p>
              </article>)}
            </div>
            <form onSubmit={sendReply} className="border-t border-slate-200 p-4">
              <div className="flex gap-3">
                <textarea data-testid="support-reply" required maxLength={5000} rows={3} value={reply}
                  onChange={(e) => setReply(e.target.value)} placeholder="Add a reply…"
                  className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2.5 text-sm" />
                <button data-testid="support-reply-submit" disabled={busy === "reply"}
                  className="inline-flex items-center gap-2 self-end rounded-lg bg-slate-950 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50">
                  <Send className="h-4 w-4" /> Send
                </button>
              </div>
            </form>
          </div> : <div className="flex min-h-[520px] items-center justify-center p-8 text-center">
            <div><ShieldCheck className="mx-auto h-10 w-10 text-blue-500" /><h2 className="mt-4 font-heading text-xl font-semibold">Account context without credential exposure</h2><p className="mt-2 max-w-md text-sm leading-6 text-slate-500">Select an existing request or create a new ticket. CleanIQ attaches identifiers and operational context—not CRM secrets.</p></div>
          </div>}
        </section>
      </div>
    </div>
  </AppShell>;
}
